/*
 * CompletenessPanel — Block A: комплектность ПД по ПП-87 (drill-down).
 * Источник: GET /projects/{id}/completeness/result (последний завершённый прогон).
 * Запуск/прогресс: POST /completeness/run + GET /completeness/run/{run_id}.
 *
 * «Только показ» (Фаза 5.1): раздел → подпункт → вердикт (раскрыто/пробел/н-п) →
 * цитата. Кнопка «В замечания» и выгрузки XLSX/DOCX — следующим шагом (5.2).
 */
import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2, XCircle, MinusCircle, ChevronDown, ChevronRight, RotateCw, ShieldCheck,
  FileSpreadsheet, FileDown, Plus, Check, Pencil, X, FileSearch,
} from 'lucide-react';
import { api } from '@/api/client';
import { DocTracePreview } from '@/components/DocTracePreview';
import { PayGateBanner } from '@/components/PayGateBanner';
import type { Project } from '@/types/api';
import { canRunCheck, runBlockReason, tariffErrorMessage, tariffStateFromProject } from '@/lib/tariff';

interface Props { projectId: string }

type Verdict = 'present' | 'absent' | 'not_applicable';

interface CItem {
  id: string;
  requirement_id: string;
  clause: string;
  text: string;
  verdict: Verdict;
  closed_by: string | null;
  node_present: boolean | null;
  absence_proven: boolean;
  evidence: string | null;
  evidence_verbatim: string | null; // дословный спан чанка — надёжный якорь search_for (15.A)
  page: number | null;
  document_id: string | null;
  document_title: string | null;
  promoted_remark_id: string | null;
}
interface CBook {
  title: string;
  ingest_status: string | null;
}
interface CSection {
  section_id: string;
  part_code: string;
  number: string;
  title: string;
  items: CItem[];
  books?: CBook[];
}
interface CResult {
  has_result: boolean;
  project_id: string;
  run_id?: string;
  finished_at?: string | null;
  matrix_version?: string;
  summary?: { total: number; present: number; absent: number; not_applicable: number };
  sections?: CSection[];
}
interface RunStart { run_id: string; status: string }
interface RunStatus {
  run_id: string;
  status: 'running' | 'done' | 'error';
  progress: { done: number; total: number; percent: number };
  findings_count: number | null;
  error: string | null;
}

const VERDICT: Record<Verdict, { label: string; color: string; bg: string; Icon: typeof CheckCircle2 }> = {
  present:        { label: 'Раскрыто',      color: '#059669', bg: '#ECFDF5', Icon: CheckCircle2 },
  absent:         { label: 'Пробел',        color: '#DC2626', bg: '#FEF2F2', Icon: XCircle },
  not_applicable: { label: 'Не применимо',  color: '#6B7280', bg: '#F3F4F6', Icon: MinusCircle },
};

function isOverlay(sectionId: string): boolean {
  return sectionId.includes('appx');
}

export function CompletenessPanel({ projectId }: Props) {
  const qc = useQueryClient();
  const [runId, setRunId] = useState<string | null>(null);
  const [verdictFilter, setVerdictFilter] = useState<Verdict | 'all'>('all');
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const [seeded, setSeeded] = useState(false);
  // 15.A Трассируемость: находка, открытая в slide-over (реальная страница + подсветка).
  const [traceItem, setTraceItem] = useState<{ item: CItem; section: CSection } | null>(null);

  const result = useQuery<CResult>({
    queryKey: ['completeness-result', projectId],
    queryFn: () => api.get<CResult>(`/projects/${projectId}/completeness/result`),
    staleTime: 1000 * 60 * 5,
  });

  // 15.E: тариф проекта — гейтинг кнопки запуска.
  const projectQ = useQuery<Project>({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    staleTime: 1000 * 30,
  });
  const tariffState = tariffStateFromProject(projectQ.data);
  const runAllowed = canRunCheck(tariffState, 'completeness');
  const runGateText = runBlockReason(tariffState, 'completeness');
  const [tariffErr, setTariffErr] = useState<string | null>(null);

  // По умолчанию раскрываем разделы, где есть пробелы (absent) — то, что важно смотреть.
  useEffect(() => {
    if (seeded || !result.data?.sections) return;
    const withGaps = result.data.sections
      .filter((s) => s.items.some((i) => i.verdict === 'absent'))
      .map((s) => s.section_id);
    setOpenSections(new Set(withGaps));
    setSeeded(true);
  }, [result.data, seeded]);

  const runStatus = useQuery<RunStatus>({
    queryKey: ['completeness-run', projectId, runId],
    queryFn: () => api.get<RunStatus>(`/projects/${projectId}/completeness/run/${runId}`),
    enabled: !!runId,
    refetchInterval: (query) => {
      const s = (query.state.data as RunStatus | undefined)?.status;
      return s === 'done' || s === 'error' ? false : 2500;
    },
  });

  useEffect(() => {
    const s = runStatus.data?.status;
    if (s === 'done') {
      setRunId(null);
      setSeeded(false);
      qc.invalidateQueries({ queryKey: ['completeness-result', projectId] });
    }
  }, [runStatus.data?.status, projectId, qc]);

  const startRun = useMutation({
    mutationFn: () => api.post<RunStart>(`/projects/${projectId}/completeness/run`, {}),
    onSuccess: (d) => { setTariffErr(null); setRunId(d.run_id); },
    onError: (e) => {
      // 15.E: тарифный гейт (402) — показываем upsell, не сырую ошибку.
      const msg = tariffErrorMessage(e);
      if (msg) { setTariffErr(msg); return; }
      setTariffErr(e instanceof Error ? e.message : 'Не удалось запустить проверку');
    },
  });

  const [promoting, setPromoting] = useState<Set<string>>(new Set());
  const promote = useMutation({
    mutationFn: (checkId: string) =>
      api.post<{ ok: boolean; remark_id: string }>(`/projects/${projectId}/completeness/requirement/${checkId}/promote`, {}),
    onSettled: (_d, _e, checkId) =>
      setPromoting((prev) => { const s = new Set(prev); s.delete(checkId); return s; }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['completeness-result', projectId] }),
  });
  const handlePromote = (checkId: string) => {
    setPromoting((prev) => new Set(prev).add(checkId));
    promote.mutate(checkId);
  };
  const handleExport = (fmt: 'xlsx' | 'docx') =>
    window.open(`/projects/${projectId}/completeness/export/${fmt}`, '_blank');

  // ── Режим разметки эталона (Фаза 5.3) ─────────────────────────────────────
  type HLabel = { verdict: string; page: string; comment: string };
  const [markupMode, setMarkupMode] = useState(false);
  const [labels, setLabels] = useState<Record<string, HLabel>>({});
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const labelsQuery = useQuery<{ sections: Record<string, Array<{ req_id: string; verdict?: string; page?: number | string | null; comment?: string }>> }>({
    queryKey: ['completeness-labels', projectId],
    queryFn: () => api.get(`/projects/${projectId}/completeness/labels`),
    enabled: markupMode,
  });
  useEffect(() => {
    const secs = labelsQuery.data?.sections;
    if (!secs) return;
    const flat: Record<string, HLabel> = {};
    for (const arr of Object.values(secs)) {
      for (const l of arr) {
        flat[l.req_id] = { verdict: l.verdict || '', page: l.page != null ? String(l.page) : '', comment: l.comment || '' };
      }
    }
    setLabels(flat);
  }, [labelsQuery.data]);
  // При включении разметки — раскрыть все разделы (чтобы метить всё подряд).
  useEffect(() => {
    const secs = result.data?.sections;
    if (markupMode && secs) {
      setOpenSections(new Set(secs.map((s) => s.section_id)));
    }
  }, [markupMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSection = (sectionId: string, current: Record<string, HLabel>) => {
    const sec = result.data?.sections?.find((s) => s.section_id === sectionId);
    if (!sec) return;
    const arr = sec.items
      .map((it) => {
        const l = current[it.requirement_id];
        if (!l || (!l.verdict && !l.page && !l.comment)) return null;
        return { req_id: it.requirement_id, clause: it.clause, verdict: l.verdict || null,
                 page: l.page ? Number(l.page) : null, comment: l.comment || '' };
      })
      .filter(Boolean);
    api.post(`/projects/${projectId}/completeness/labels`, { section_id: sectionId, labels: arr })
      .then(() => setSavedAt(new Date().toLocaleTimeString('ru-RU')))
      .catch(() => {});
  };
  const setLabel = (reqId: string, sectionId: string, patch: Partial<HLabel>) => {
    setLabels((prev) => {
      const base: HLabel = prev[reqId] || { verdict: '', page: '', comment: '' };
      const next = { ...prev, [reqId]: { ...base, ...patch } };
      if (saveTimers.current[sectionId]) clearTimeout(saveTimers.current[sectionId]);
      saveTimers.current[sectionId] = setTimeout(() => saveSection(sectionId, next), 800);
      return next;
    });
  };

  const running = !!runId && runStatus.data?.status !== 'error';
  const runErr = runStatus.data?.status === 'error';

  // ── Прогресс прогона ──────────────────────────────────────────────────────
  const progressBar = running && (
    <div style={{ margin: '16px 0', maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
        <span className="text-ink" style={{ fontWeight: 600 }}>Идёт проверка комплектности…</span>
        <span className="text-muted">
          {runStatus.data ? `${runStatus.data.progress.done} / ${runStatus.data.progress.total}` : '…'}
        </span>
      </div>
      <div style={{ height: 8, borderRadius: 6, background: '#E5E7EB', overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${runStatus.data?.progress.percent ?? 4}%`,
          background: '#0A1628', transition: 'width .4s ease',
        }} />
      </div>
      <p className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
        Судья ИИ проверяет ~112 требований ПП-87 — обычно 1–3 минуты. Можно уйти со страницы, прогон идёт в фоне.
      </p>
    </div>
  );

  // ── Загрузка ──────────────────────────────────────────────────────────────
  if (result.isLoading) {
    return <div style={{ padding: 40, textAlign: 'center' }} className="text-muted">Загрузка результата…</div>;
  }

  const data = result.data;

  // 15.E: баннер тарифного гейта (upsell) + ошибка запуска.
  const gateBanner = (!runAllowed && runGateText)
    ? <PayGateBanner projectId={projectId} reason={runGateText} /> : null;
  const errBanner = tariffErr ? (
    <p style={{ color: '#B45309', fontSize: 13, margin: '8px 0 0', maxWidth: 720 }}>{tariffErr}</p>
  ) : null;

  // ── Ещё не проверяли ──────────────────────────────────────────────────────
  if (data && !data.has_result) {
    return (
      <div style={{ maxWidth: 720 }}>
        <PanelHeader onRun={() => startRun.mutate()} running={running} startPending={startRun.isPending}
                     hasResult={false} runDisabled={!runAllowed} />
        {gateBanner}
        {progressBar}
        {!running && (
          <div style={{ textAlign: 'center', padding: '40px 20px', border: '1px dashed #E5E7EB', borderRadius: 12, marginTop: 8 }}>
            <ShieldCheck size={32} style={{ color: '#9CA3AF' }} />
            <p className="text-ink" style={{ fontSize: 15, fontWeight: 600, margin: '12px 0 4px' }}>
              Проверка комплектности ещё не запускалась
            </p>
            <p className="text-muted" style={{ fontSize: 13, maxWidth: 460, margin: '0 auto 16px' }}>
              Движок сверит проект с обязательным составом ПП-87 — по каждому требованию покажет,
              раскрыто оно в документации или это пробел.
            </p>
            <button type="button" onClick={() => startRun.mutate()}
                    disabled={startRun.isPending || !runAllowed}
                    style={{ ...primaryBtn, opacity: (startRun.isPending || !runAllowed) ? 0.5 : 1,
                             cursor: (startRun.isPending || !runAllowed) ? 'not-allowed' : 'pointer' }}>
              {startPendingLabel(startRun.isPending)}
            </button>
            {errBanner}
          </div>
        )}
      </div>
    );
  }

  if (!data || !data.summary) return null;
  const { summary, sections = [] } = data;

  // ── Фильтр ────────────────────────────────────────────────────────────────
  const visibleSections = sections
    .map((s) => ({
      ...s,
      items: verdictFilter === 'all' ? s.items : s.items.filter((i) => i.verdict === verdictFilter),
    }))
    .filter((s) => s.items.length > 0);

  return (
    <div style={{ maxWidth: 900 }}>
      <PanelHeader
        onRun={() => startRun.mutate()}
        running={running}
        startPending={startRun.isPending}
        hasResult
        runDisabled={!runAllowed}
        finishedAt={data.finished_at}
        matrixVersion={data.matrix_version}
        onExport={handleExport}
      />
      {gateBanner}
      {errBanner}

      {/* Режим разметки эталона */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 12px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setMarkupMode((v) => !v)}
          style={{ ...iconBtn, ...(markupMode ? { background: '#0A1628', color: '#fff', borderColor: '#0A1628' } : {}) }}
        >
          <Pencil size={14} /><span style={{ fontSize: 12 }}>{markupMode ? 'Разметка: вкл' : 'Режим разметки'}</span>
        </button>
        {markupMode && (
          <span className="text-muted" style={{ fontSize: 11 }}>
            по каждому пункту: описано / не_описано / н-п + стр. + комментарий · автосохранение
            {savedAt ? ` · сохранено ${savedAt}` : ''}
          </span>
        )}
      </div>

      {runErr && (
        <div style={{ margin: '12px 0', padding: '10px 14px', borderRadius: 8, background: '#FEF2F2', color: '#B91C1C', fontSize: 13 }}>
          Прогон завершился ошибкой{runStatus.data?.error ? `: ${runStatus.data.error}` : ''}.
        </div>
      )}
      {progressBar}

      {/* Сводка */}
      <div style={{ display: 'flex', gap: 24, padding: '16px 20px', border: '1px solid #E5E7EB', borderRadius: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat value={summary.total} label="Всего" color="#0A1628" />
        <Stat value={summary.absent} label="Пробелов" color={summary.absent > 0 ? '#DC2626' : '#0A1628'} />
        <Stat value={summary.present} label="Раскрыто" color="#059669" />
        <Stat value={summary.not_applicable} label="Не применимо" color="#6B7280" />
      </div>

      {/* Фильтр */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="text-muted" style={{ fontSize: 12 }}>Фильтр:</span>
        {(['all', 'absent', 'present', 'not_applicable'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setVerdictFilter(v)}
            style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer',
              border: `1px solid ${verdictFilter === v ? '#0A1628' : '#E5E7EB'}`,
              background: verdictFilter === v ? '#0A1628' : 'transparent',
              color: verdictFilter === v ? '#fff' : '#374151',
            }}
          >
            {v === 'all' ? 'Все' : VERDICT[v].label}
          </button>
        ))}
      </div>

      {/* Разделы */}
      {visibleSections.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>Нет пунктов по выбранному фильтру.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visibleSections.map((s) => {
            const open = openSections.has(s.section_id);
            const gaps = s.items.filter((i) => i.verdict === 'absent').length;
            return (
              <div key={s.section_id} style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                <button
                  type="button"
                  onClick={() => setOpenSections((prev) => toggle(prev, s.section_id))}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                    background: '#FAFAFA', border: 'none', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span className="text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
                    Раздел {s.number}. {s.title}
                  </span>
                  {isOverlay(s.section_id) && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#92400E', background: '#FEF3C7', padding: '2px 8px', borderRadius: 20 }}>
                      Приложение №3
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                    {gaps > 0 && (
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#DC2626' }}>{gaps} проб.</span>
                    )}
                    <span className="text-muted" style={{ fontSize: 12 }}>{s.items.length} пункт.</span>
                    {s.books && s.books.length > 0 && (
                      <span className="text-muted" style={{ fontSize: 12 }}>· {s.books.length} кн.</span>
                    )}
                  </span>
                </button>
                {open && (
                  <div>
                    {s.books && s.books.length > 0 && (
                      <div style={{ padding: '10px 14px', borderTop: '1px solid #F3F4F6', background: '#FCFCFD', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        <span className="text-muted" style={{ fontSize: 11, fontWeight: 600 }}>
                          Книги раздела ({s.books.length}):
                        </span>
                        {s.books.map((b) => {
                          const ok = b.ingest_status === 'ready';
                          return (
                            <span key={b.title} title={ok ? 'загружена' : `статус: ${b.ingest_status || '—'}`} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11,
                              padding: '2px 8px', borderRadius: 20,
                              background: ok ? '#ECFDF5' : '#FEF2F2', color: ok ? '#059669' : '#DC2626',
                            }}>
                              {ok ? <Check size={11} /> : <XCircle size={11} />}
                              {b.title}{ok ? '' : ` · ${b.ingest_status || '—'}`}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {s.items.map((it) => {
                      const cfg = VERDICT[it.verdict];
                      const key = `${s.section_id}::${it.clause}`;
                      const itemOpen = openItems.has(key);
                      const hasDetail = !!it.evidence;
                      return (
                        <div key={key} style={{ borderTop: '1px solid #F3F4F6' }}>
                          <button
                            type="button"
                            onClick={() => hasDetail && setOpenItems((prev) => toggle(prev, key))}
                            style={{
                              width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px 10px 40px',
                              background: 'transparent', border: 'none', textAlign: 'left', cursor: hasDetail ? 'pointer' : 'default',
                            }}
                          >
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
                              fontSize: 11, fontWeight: 600, color: cfg.color, background: cfg.bg,
                              padding: '2px 8px', borderRadius: 20, marginTop: 1,
                            }}>
                              <cfg.Icon size={12} /> {cfg.label}
                            </span>
                            <span style={{ minWidth: 0 }}>
                              <span className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>{it.clause}</span>
                              <span className="text-muted" style={{ fontSize: 13, display: 'block', marginTop: 2 }}>
                                {it.text.length > 220 && !itemOpen ? `${it.text.slice(0, 220)}…` : it.text}
                              </span>
                            </span>
                            {hasDetail && (
                              <span style={{ marginLeft: 'auto', flexShrink: 0, marginTop: 2 }}>
                                {itemOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </span>
                            )}
                          </button>
                          {/* 15.A: показать находку в реальном документе (slide-over) */}
                          <div style={{ padding: '0 14px 8px 40px' }}>
                            <button
                              type="button"
                              onClick={() => setTraceItem({ item: it, section: s })}
                              style={{ ...iconBtn, padding: '5px 10px' }}
                              title={it.document_id
                                ? 'Открыть находку и страницу документа с подсветкой цитаты'
                                : 'Открыть текст требования (в документации не раскрыто)'}
                            >
                              <FileSearch size={13} />
                              {/* Пробел/н-п не имеют места в документе → не обещаем «Показать». */}
                              <span style={{ fontSize: 12 }}>{it.document_id ? 'Показать в документе' : 'Открыть требование'}</span>
                            </button>
                          </div>
                          {markupMode && (
                            <div style={{ padding: '0 14px 12px 40px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                              {(['описано', 'не_описано', 'н_п'] as const).map((v) => {
                                const active = labels[it.requirement_id]?.verdict === v;
                                return (
                                  <button
                                    key={v}
                                    type="button"
                                    onClick={() => setLabel(it.requirement_id, s.section_id, { verdict: active ? '' : v })}
                                    style={{
                                      padding: '3px 10px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                                      border: `1px solid ${active ? labelColor(v) : '#E5E7EB'}`,
                                      background: active ? labelColor(v) : 'transparent',
                                      color: active ? '#fff' : '#374151',
                                    }}
                                  >
                                    {v === 'н_п' ? 'н-п' : v}
                                  </button>
                                );
                              })}
                              <input
                                type="number" placeholder="стр."
                                value={labels[it.requirement_id]?.page || ''}
                                onChange={(e) => setLabel(it.requirement_id, s.section_id, { page: e.target.value })}
                                style={{ width: 64, fontSize: 12, padding: '4px 8px', border: '1px solid #E5E7EB', borderRadius: 8 }}
                              />
                              <input
                                type="text" placeholder="комментарий"
                                value={labels[it.requirement_id]?.comment || ''}
                                onChange={(e) => setLabel(it.requirement_id, s.section_id, { comment: e.target.value })}
                                style={{ flex: 1, minWidth: 160, fontSize: 12, padding: '4px 8px', border: '1px solid #E5E7EB', borderRadius: 8 }}
                              />
                            </div>
                          )}
                          {it.verdict === 'absent' && (
                            <div style={{ padding: '0 14px 10px 40px' }}>
                              {it.promoted_remark_id ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: '#059669' }}>
                                  <Check size={13} /> В замечании
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handlePromote(it.id)}
                                  disabled={promoting.has(it.id)}
                                  style={{ ...iconBtn, padding: '5px 10px', opacity: promoting.has(it.id) ? 0.6 : 1 }}
                                >
                                  <Plus size={13} />
                                  <span style={{ fontSize: 12 }}>{promoting.has(it.id) ? 'Добавляем…' : 'В замечания'}</span>
                                </button>
                              )}
                            </div>
                          )}
                          {itemOpen && it.evidence && (
                            <div style={{ padding: '0 14px 12px 40px' }}>
                              <div style={{
                                fontSize: 12, color: '#374151', background: '#F9FAFB',
                                borderLeft: '3px solid #059669', padding: '8px 12px', borderRadius: 6,
                              }}>
                                <span style={{ fontStyle: 'italic' }}>«{it.evidence}»</span>
                                {(it.document_title || it.page != null || it.closed_by) && (
                                  <span className="text-muted" style={{ display: 'block', marginTop: 4, fontSize: 11 }}>
                                    {[
                                      it.document_title,
                                      it.page != null ? `стр. ${it.page}` : null,
                                      it.closed_by ? modalityLabel(it.closed_by) : null,
                                    ].filter(Boolean).join(' · ')}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {traceItem && (
        <TraceSlideOver
          projectId={projectId}
          item={traceItem.item}
          section={traceItem.section}
          onClose={() => setTraceItem(null)}
        />
      )}
    </div>
  );
}

// ── 15.A Трассируемость: slide-over с находкой + реальной страницей ─────────
// Ядро подсветки вынесено в DocTracePreview (переиспользуется нормами/решениями/
// заданием). Здесь — только специфика комплектности: шапка + «Спросить специалиста».

function TraceSlideOver({ item, section, onClose }: { projectId: string; item: CItem; section: CSection; onClose: () => void }) {
  const cfg = VERDICT[item.verdict];

  // Esc закрывает.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 z-40" style={{ background: 'rgba(10,22,40,.3)' }} onClick={onClose} />
      <aside
        className="fixed top-0 right-0 h-full bg-white border-l border-border flex flex-col z-50"
        style={{ width: 560, maxWidth: '100%', boxShadow: '-16px 0 32px rgba(10,22,40,.12)' }}
        role="dialog"
        aria-label="Находка и место в документе"
      >
        {/* Шапка */}
        <header className="border-b border-border flex items-start justify-between" style={{ padding: '18px 22px', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h4 className="font-heading text-ink flex items-center" style={{ fontSize: 17, fontWeight: 700, gap: 8, margin: 0 }}>
              <FileSearch size={16} /> Находка в документе
            </h4>
            <p className="text-muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
              Раздел {section.number}. {section.title}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-muted" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} aria-label="Закрыть">
            <X size={20} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto" style={{ padding: '16px 22px' }}>
          {/* ВЕРХ: находка целиком */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 12, fontWeight: 600, color: cfg.color, background: cfg.bg,
              padding: '3px 10px', borderRadius: 20,
            }}>
              <cfg.Icon size={13} /> {cfg.label}
            </span>
            <span className="rounded-pill" style={{ padding: '3px 10px', fontSize: 12, fontWeight: 600, background: 'rgba(245,158,11,.12)', color: '#B45309' }}>
              ПП-87 · {item.clause}
            </span>
          </div>

          <div className="text-ink" style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 4 }}>
            {item.text}
          </div>
          {item.document_title && (
            <p className="text-muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
              Документ: {item.document_title}{item.page != null ? ` · стр. ${item.page}` : ''}
              {item.closed_by ? ` · ${modalityLabel(item.closed_by)}` : ''}
            </p>
          )}

          {item.evidence && (
            <div style={{
              marginTop: 12, fontSize: 13, color: '#374151', background: '#F9FAFB',
              borderLeft: `3px solid ${cfg.color}`, padding: '10px 14px', borderRadius: 6, fontStyle: 'italic',
            }}>
              «{item.evidence}»
            </div>
          )}

          {/* НИЗ: реальная страница документа с подсветкой (общее ядро).
              Цитата: дословный спан чанка (evidence_verbatim) надёжнее пересказа
              LLM (evidence) для search_for; page известен у комплектности. */}
          {item.document_id ? (
            <div style={{ marginTop: 20 }}>
              <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', marginBottom: 10 }}>
                Место в документе
              </div>
              <DocTracePreview
                documentId={item.document_id}
                page={item.page}
                quote={item.evidence_verbatim || item.evidence || ''}
              />
            </div>
          ) : (
            // Пробел/н-п: места в документе НЕ существует по определению (нечего
            // раскрывать) — вместо «источник не привязан» честно объясняем причину.
            <div className="text-muted" style={{
              marginTop: 20, fontSize: 13, lineHeight: 1.5, padding: '14px 16px',
              background: '#F9FAFB', border: '1px dashed #E5E7EB', borderRadius: 10,
            }}>
              {item.verdict === 'absent'
                ? 'Требование не раскрыто в документации — это и есть пробел. Показывать в документе нечего; при необходимости добавьте его в замечания.'
                : item.verdict === 'not_applicable'
                ? 'Требование неприменимо к объекту — привязки к документу нет.'
                : 'У находки нет привязки к документу.'}
            </div>
          )}
        </div>
      </aside>

    </>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────

function toggle(set: Set<string>, key: string): Set<string> {
  const s = new Set(set);
  if (s.has(key)) s.delete(key); else s.add(key);
  return s;
}

function modalityLabel(m: string): string {
  return m === 'table' ? 'таблица' : m === 'drawing' ? 'чертёж' : 'текст';
}

function labelColor(v: string): string {
  return v === 'описано' ? '#059669' : v === 'не_описано' ? '#DC2626' : '#6B7280';
}

function startPendingLabel(pending: boolean): string {
  return pending ? 'Запускаем…' : 'Проверить комплектность';
}

function PanelHeader({
  onRun, running, startPending, hasResult, finishedAt, matrixVersion, onExport, runDisabled,
}: {
  onRun: () => void; running: boolean; startPending: boolean; hasResult: boolean;
  finishedAt?: string | null; matrixVersion?: string; onExport?: (fmt: 'xlsx' | 'docx') => void;
  runDisabled?: boolean;  // 15.E: тариф не позволяет запуск
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
      <div>
        <h4 className="font-heading text-ink" style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
          Комплектность по ПП-87
        </h4>
        <p className="text-muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
          Постановление Правительства РФ № 87 — обязательный состав проектной документации
          {finishedAt && ` · Проверено ${new Date(finishedAt).toLocaleString('ru-RU')}`}
          {matrixVersion && ` · ${matrixVersion}`}
        </p>
      </div>
      {hasResult && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {onExport && (
            <>
              <button type="button" onClick={() => onExport('xlsx')} style={iconBtn} title="Скачать XLSX">
                <FileSpreadsheet size={14} /><span style={{ fontSize: 12 }}>XLSX</span>
              </button>
              <button type="button" onClick={() => onExport('docx')} style={iconBtn} title="Скачать DOCX">
                <FileDown size={14} /><span style={{ fontSize: 12 }}>DOCX</span>
              </button>
            </>
          )}
          <button type="button" onClick={onRun} disabled={running || startPending || !!runDisabled}
                  style={{ ...iconBtn, opacity: (running || startPending || runDisabled) ? 0.5 : 1,
                           cursor: runDisabled ? 'not-allowed' : 'pointer' }}>
            <RotateCw size={14} style={{ animation: running ? 'spin 1s linear infinite' : 'none' }} />
            <span style={{ fontSize: 12 }}>{running ? 'Проверяем…' : 'Проверить заново'}</span>
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div>
      <div style={{ fontSize: 24, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>{label}</div>
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px',
  borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', color: '#374151', cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px',
  borderRadius: 8, border: 'none', background: '#0A1628', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
